/**
 * Routes of area (g) — saas. OWNED BY area (g) — saas. Paths unchanged (boards.ts hrefs point at them).
 * Search params carry every filter and window (`?fenetre=`) so a reload or the browser's back button restores the
 * view (boards' « Recharger » / « Retour »). Unknown values fall back to the default instead of failing the route.
 */
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { boardsAt } from "@/boards/board-switch";
import { guard, staffRoute } from "@/app/route-base";

const opt = <T extends z.ZodType>(s: T) => s.optional().catch(undefined);
const Month = z.string().regex(/^\d{4}-\d{2}$/);
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const OverviewSearch = z.object({ mois: opt(Month), fenetre: opt(z.enum(["export"])) });
export const AccountsSearch = z.object({
  q: opt(z.string()),
  plan: opt(z.enum(["start", "pro", "business"])),
  statut: opt(z.enum(["active", "quota_reached", "restricted", "suspended"])),
  einv: opt(z.enum(["registered", "in_progress", "not_connected", "error"])),
  du: opt(Day),
  au: opt(Day),
  quota: opt(z.boolean()),
  mois: opt(Month),
  page: opt(z.coerce.number().int().min(1).max(20)),
  fenetre: opt(z.enum(["filtres", "export"])),
});
export type AccountsSearch = z.infer<typeof AccountsSearch>;
export const AccountSearch = z.object({
  onglet: opt(z.enum(["all", "invoices", "quotes", "credit_notes", "watch"])),
  page: opt(z.coerce.number().int().min(1).max(20)),
  fenetre: opt(z.enum(["debloquer", "renvoyer", "quota", "desactiver", "reactiver", "exporter"])),
  document: opt(z.string()),
});
export type AccountSearch = z.infer<typeof AccountSearch>;
export const FlowSearch = z.object({
  q: opt(z.string()),
  statut: opt(z.enum(["submitted", "in_progress", "rejected", "refused", "paid", "not_transmitted"])),
  periode: opt(z.enum(["today", "7d", "30d"])),
  page: opt(z.coerce.number().int().min(1).max(20)),
  document: opt(z.string()),
  compte: opt(z.string()),
  fenetre: opt(z.enum(["renvoyer", "export"])),
});
export type FlowSearch = z.infer<typeof FlowSearch>;
export const IncidentsSearch = z.object({
  q: opt(z.string()),
  type: opt(z.enum(["support", "reminder", "email"])),
  etat: opt(z.enum(["open", "resolved", "all"])),
  page: opt(z.coerce.number().int().min(1).max(20)),
  incident: opt(z.string()),
  fenetre: opt(z.enum(["repondre", "resoudre", "relance"])),
});
export type IncidentsSearch = z.infer<typeof IncidentsSearch>;
export const SettingsSearch = z.object({ fenetre: opt(z.enum(["enregistrer", "quotas", "iopole"])), plan: opt(z.enum(["start", "pro", "business"])) });
export type SettingsSearch = z.infer<typeof SettingsSearch>;

/** FS-Vue */
export const facturationRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation",
  validateSearch: OverviewSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation"),
});

/** FS-Comptes */
export const facturationComptesRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation/comptes",
  validateSearch: AccountsSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation/comptes"),
});

/** FS-Fiche */
export const facturationComptesCompanyidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation/comptes/$companyId",
  validateSearch: AccountSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation/comptes/$companyId"),
});

/** FS-Iopole */
export const facturationFluxRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation/flux",
  validateSearch: FlowSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation/flux"),
});

/** FS-Incidents */
export const facturationIncidentsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation/incidents",
  validateSearch: IncidentsSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation/incidents"),
});

/** FS-Parametres */
export const facturationParametresRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facturation/parametres",
  validateSearch: SettingsSearch,
  beforeLoad: guard("saas", "read"),
  component: boardsAt("/facturation/parametres"),
});

export const saasRoutes = [facturationRoute, facturationComptesRoute, facturationComptesCompanyidRoute, facturationFluxRoute, facturationIncidentsRoute, facturationParametresRoute] as const;
